import { ValidationQuestion } from "../interfaces/document-category.interface";

export interface QuestionAnswerEntry {
  value?: unknown;
  risky?: boolean;
  inconsistency?: boolean;
  [key: string]: unknown;
}

export interface AnswerExtractionQuestion {
  type?: string;
  multiple_choice?: boolean;
  upload_area_config?: { multiple?: boolean };
  options?: ReadonlyArray<{ value?: unknown }>;
}

export interface ExtractFormValueOptions {
  multiple?: boolean;
  question?: AnswerExtractionQuestion | null;
}

function isEmptyAnswerValue(value: unknown): boolean {
  if (value == null || value === "") {
    return true;
  }

  return (
    value != null && typeof value === "object" && !Array.isArray(value) && Object.keys(value as object).length === 0
  );
}

function extractAnswerEntryValue(item: unknown): unknown {
  if (item != null && typeof item === "object" && "value" in item) {
    return (item as QuestionAnswerEntry).value;
  }
  return item;
}

function shouldExtractAnswerAsMultiple(question: AnswerExtractionQuestion | null | undefined): boolean {
  return question?.multiple_choice === true || question?.type === "upload-area";
}

/** Extracts the form/display value from legacy or v2 persisted answer shapes. */
export function extractFormValueFromAnswer(answer: unknown, options?: ExtractFormValueOptions): unknown {
  const multiple = options?.multiple === true;
  const isUpload = options?.question?.type === "upload-area";

  if (answer == null) {
    return multiple ? [] : null;
  }

  if (Array.isArray(answer)) {
    if (!answer.length) {
      return multiple ? [] : null;
    }

    const entries = answer.map((item) => extractAnswerEntryValue(item));
    if (isUpload) {
      return multiple ? answer : answer[0];
    }
    return multiple ? entries : entries[0] ?? null;
  }

  if (typeof answer === "object" && answer !== null) {
    if (!("value" in answer)) {
      return multiple ? [] : null;
    }

    const innerValue = (answer as QuestionAnswerEntry).value;
    if (isEmptyAnswerValue(innerValue)) {
      return multiple ? [] : null;
    }
    return innerValue;
  }

  return answer;
}

/** Returns true when a question has a persisted answer (legacy object or v2 array format). */
export function hasStoredQuestionAnswer(question: Partial<ValidationQuestion> | null | undefined): boolean {
  if (!question) {
    return false;
  }

  const extracted = extractFormValueFromAnswer(question.answer, {
    multiple: shouldExtractAnswerAsMultiple(question),
    question,
  });

  if (Array.isArray(extracted)) {
    return extracted.length > 0;
  }

  return extracted != null && extracted !== "";
}

/** Returns true when a question has displayable text in v1 or v2 shapes. */
export function hasResolvableQuestionDisplayText(question: Partial<ValidationQuestion> | null | undefined): boolean {
  if (!question) {
    return false;
  }

  return !!(
    question.formatted_question?.trim() ||
    question.question?.trim() ||
    question.instructions?.message?.trim() ||
    question.header?.title?.trim() ||
    question.question_data?.length ||
    // Follow-ups (dropdown/input/textarea/checkbox) often only expose label/title/message.
    (typeof question.label === "string" && question.label.trim()) ||
    (typeof question.title === "string" && question.title.trim()) ||
    (typeof question.message === "string" && question.message.trim())
  );
}

/** Returns true when a question has saved map captures. */
export function hasMapCaptureEvidence(question: Partial<ValidationQuestion> | null | undefined): boolean {
  return (question?.map_config?.captures?.length ?? 0) > 0;
}

/** Returns true when a question should be shown in read-only overview displays. */
export function hasQuestionDisplayEvidence(question: Partial<ValidationQuestion> | null | undefined): boolean {
  return hasStoredQuestionAnswer(question) || hasMapCaptureEvidence(question);
}

/** Resolves the best available question text for read-only displays. */
export function resolveQuestionDisplayText(question: Partial<ValidationQuestion> | null | undefined): string {
  if (!question) {
    return "";
  }

  const label = typeof question.label === "string" ? question.label.trim() : "";
  const title = typeof question.title === "string" ? question.title.trim() : "";
  const message = typeof question.message === "string" ? question.message.trim() : "";

  return (
    question.formatted_question?.trim() ||
    question.question?.trim() ||
    question.instructions?.message?.trim() ||
    question.header?.title?.trim() ||
    title ||
    label ||
    message ||
    ""
  );
}

/** Resolves answer metadata flags from legacy or v2 answer payloads. */
export function resolveAnswerMetadata(answer: unknown): { risky: boolean; inconsistency: boolean } {
  const entry = Array.isArray(answer) ? answer[0] : answer;
  if (entry != null && typeof entry === "object") {
    return {
      risky: (entry as QuestionAnswerEntry).risky === true,
      inconsistency: (entry as QuestionAnswerEntry).inconsistency === true,
    };
  }

  return { risky: false, inconsistency: false };
}

/** Maps saved map captures to human-readable labels for overview displays. */
export function resolveMapCaptureLabels(question: Partial<ValidationQuestion> | null | undefined): string[] {
  return (question?.map_config?.captures ?? [])
    .map((capture) => capture.success_message?.trim())
    .filter((label): label is string => !!label);
}
